import { useEffect, useState } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTA } from "@/components/CTA";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ShoppingCart, Repeat, Puzzle, Gauge, Palette, Headphones, Globe, Search, Shield,
  Layers, Zap, CreditCard, BarChart3, Bot, MessageSquare, TrendingUp, Lock,
  Percent, Package, Building2, RefreshCw, Gift, ArrowRight, Sparkles, Users, Mail,
  Code, Settings, FileSearch, Truck, ExternalLink,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { usePageSEO } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

import shopifyLogo from "@/assets/partners/shopify.svg";
import webflowLogo from "@/assets/partners/webflow.svg";
import wordpressLogo from "@/assets/partners/wordpress.svg";
import woocommerceLogo from "@/assets/partners/woocommerce.svg";
import notionLogo from "@/assets/partners/notion.svg";
import lovableLogo from "@/assets/partners/lovable.svg";

const ICONS: Record<string, any> = {
  ShoppingCart, Repeat, Puzzle, Gauge, Palette, Headphones, Globe, Search, Shield,
  Layers, Zap, CreditCard, BarChart3, Bot, MessageSquare, TrendingUp, Lock,
  Percent, Package, Building2, RefreshCw, Gift, ArrowRight, Sparkles, Users, Mail,
  Code, Settings, FileSearch, Truck,
};

interface PlatformMeta {
  logo: string;
  affiliateLink?: string;
  accentClass: string;
  accentBg: string;
  accentBorder: string;
}

const PLATFORM_MAP: Record<string, PlatformMeta> = {
  "/shopify": {
    logo: shopifyLogo,
    affiliateLink: "https://www.shopify.com/partners/directory/partner/wavai-technology-llc",
    accentClass: "text-[hsl(134,50%,40%)]",
    accentBg: "bg-[hsl(134,50%,40%,0.08)]",
    accentBorder: "border-[hsl(134,50%,40%,0.2)]",
  },
  "/webflow": {
    logo: webflowLogo,
    affiliateLink: "https://try.webflow.com/wavai",
    accentClass: "text-[hsl(233,100%,60%)]",
    accentBg: "bg-[hsl(233,100%,60%,0.08)]",
    accentBorder: "border-[hsl(233,100%,60%,0.2)]",
  },
  "/wordpress": {
    logo: wordpressLogo,
    accentClass: "text-[hsl(200,60%,36%)]",
    accentBg: "bg-[hsl(200,60%,36%,0.08)]",
    accentBorder: "border-[hsl(200,60%,36%,0.2)]",
  },
  "/woocommerce": {
    logo: woocommerceLogo,
    accentClass: "text-[hsl(290,30%,43%)]",
    accentBg: "bg-[hsl(290,30%,43%,0.08)]",
    accentBorder: "border-[hsl(290,30%,43%,0.2)]",
  },
  "/solutions/notion": {
    logo: notionLogo,
    affiliateLink: "https://affiliate.notion.so/krt78pjxec3w",
    accentClass: "text-foreground",
    accentBg: "bg-secondary",
    accentBorder: "border-border",
  },
  "/lovable": {
    logo: lovableLogo,
    affiliateLink: "https://lovable.dev/invite/QQ4D6MX",
    accentClass: "text-[hsl(340,80%,55%)]",
    accentBg: "bg-[hsl(340,80%,55%,0.08)]",
    accentBorder: "border-[hsl(340,80%,55%,0.2)]",
  },
};

/** Maps URL path → platform name used in work.platforms */
const PATH_TO_PLATFORM: Record<string, string> = {
  "/shopify": "Shopify",
  "/webflow": "Webflow",
  "/wordpress": "WordPress",
  "/woocommerce": "WooCommerce",
  "/lovable": "Lovable",
};

interface PageRecord {
  title_en: string;
  title_ar: string | null;
  seo_title_en: string | null;
  seo_title_ar: string | null;
  seo_description_en: string | null;
  seo_description_ar: string | null;
  sections_json: any;
  status: string;
}

export default function ServicePage() {
  const location = useLocation();
  const { lang, t, localePath } = useLanguage();
  const isAr = lang === "ar";
  const [page, setPage] = useState<PageRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [relatedWork, setRelatedWork] = useState<any[]>([]);

  const cleanPath = location.pathname.replace(/^\/ar/, "") || "/";
  const platform = PLATFORM_MAP[cleanPath] || PLATFORM_MAP["/" + cleanPath.split("/").filter(Boolean)[0]];

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("pages")
        .select("title_en, title_ar, seo_title_en, seo_title_ar, seo_description_en, seo_description_ar, sections_json, status")
        .eq("path", cleanPath)
        .eq("status", "published")
        .single();
      if (!data) { setNotFound(true); setLoading(false); return; }
      setPage(data as PageRecord);
      setLoading(false);
    };
    fetchPage();
  }, [location.pathname, isAr]);

  const seoTitle = page ? (isAr ? (page.seo_title_ar || page.seo_title_en) : page.seo_title_en) : null;
  const seoDesc = page ? (isAr ? (page.seo_description_ar || page.seo_description_en) : page.seo_description_en) : null;
  const base = "https://www.wavai.ae";

  useCmsPageSEO({
    title: seoTitle || (page ? (isAr ? (page.title_ar || page.title_en) : page.title_en) + " | Wavai" : "Wavai"),
    description: seoDesc || "",
    canonical: `${base}${cleanPath}`,
    hreflang: [
      { lang: "en", href: `${base}${cleanPath}` },
      { lang: "ar", href: `${base}/ar${cleanPath}` },
      { lang: "x-default", href: `${base}${cleanPath}` },
    ],
  }, cleanPath);

  // Fetch related work projects by platform
  const platformName = PATH_TO_PLATFORM[cleanPath];
  useEffect(() => {
    if (!platformName) { setRelatedWork([]); return; }
    supabase
      .from("work")
      .select("id, title, title_ar, slug, featured_image, short_description, short_description_ar")
      .contains("platforms", [platformName])
      .eq("status", "published")
      .order("sort_order")
      .limit(6)
      .then(({ data }) => setRelatedWork(data || []));
  }, [platformName]);

  if (loading) return <div className="min-h-screen bg-background"><Header /><div className="pt-32 text-center text-muted-foreground">Loading...</div><Footer /></div>;
  if (notFound) {
    // If a sub-page record is missing, fall back to its parent section instead of 404
    const segments = cleanPath.split("/").filter(Boolean);
    if (segments.length > 1) {
      return <Navigate to={localePath("/" + segments[0])} replace />;
    }
    return <Navigate to="/404" replace />;
  }
  if (!page) return null;

  const s = page.sections_json || {};
  const hero = isAr ? (s.hero_ar || s.hero) : s.hero;
  const why = isAr ? (s.why_ar || s.why) : s.why;
  const features = isAr ? (s.features_ar || s.features) : s.features;
  const pricing = isAr ? (s.pricing_ar || s.pricing) : s.pricing;
  const faqs = isAr ? (s.faqs_ar || s.faqs) : s.faqs;

  const LogoElement = platform ? (
    <motion.div
      className="flex items-center justify-center mb-6"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {platform.affiliateLink ? (
        <a
          href={platform.affiliateLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`group inline-flex items-center gap-3 px-5 py-3 rounded-2xl border ${platform.accentBorder} ${platform.accentBg} transition-all duration-300 hover:scale-105 hover:shadow-lg`}
        >
          <img
            src={platform.logo}
            alt={cleanPath.replace("/", "").replace("solutions/", "") + " logo"}
            className="h-8 sm:h-10 w-auto object-contain"
          />
          <ExternalLink className={`h-4 w-4 ${platform.accentClass} opacity-0 group-hover:opacity-60 transition-opacity duration-300`} />
        </a>
      ) : (
        <div className={`inline-flex items-center px-5 py-3 rounded-2xl border ${platform.accentBorder} ${platform.accentBg}`}>
          <img
            src={platform.logo}
            alt={cleanPath.replace("/", "") + " logo"}
            className="h-8 sm:h-10 w-auto object-contain"
          />
        </div>
      )}
    </motion.div>
  ) : null;

  const pathSegments = cleanPath.split("/").filter(Boolean);
  const isSubPage = pathSegments.length > 1;
  const displayTitle = isAr ? (page.title_ar || page.title_en) : page.title_en;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 lg:pt-24">

        {/* Hero */}
        {hero && (
          <section className={isSubPage ? "py-12 lg:py-32" : "py-12 lg:py-32"}>
            <div className="container px-6 max-w-3xl mx-auto text-center">
              {LogoElement}
              {hero.badge && (
                <motion.p
                  className="text-sm uppercase tracking-wider text-primary font-medium mb-4"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  {hero.badge}
                </motion.p>
              )}
              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 text-balance"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
              >
                {hero.title}{" "}
                {hero.highlight && <span className="text-gradient">{hero.highlight}</span>}
              </motion.h1>
              {hero.description && (
                <motion.p
                  className="text-lg text-muted-foreground leading-relaxed mb-8 text-balance"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                >
                  {hero.description}
                </motion.p>
              )}
              {hero.cta_text && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.35 }}
                >
                  <Button variant="hero" size="lg" asChild>
                    <Link to={hero.cta_link || "/contact"}>
                      {hero.cta_text}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </motion.div>
              )}
            </div>
          </section>
        )}

        {/* Why Section */}
        {why && (
          <section className="py-12 lg:py-32 bg-secondary/40">
            <div className="container px-6 max-w-3xl mx-auto">
              <motion.h2
                className="text-3xl sm:text-4xl font-display font-bold mb-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                {why.title}
              </motion.h2>
              <motion.p
                className="text-muted-foreground text-lg leading-relaxed text-center"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {why.description}
              </motion.p>
            </div>
          </section>
        )}

        {/* Features */}
        {features && features.items && features.items.length > 0 && (
          <section className="py-12 lg:py-32">
            <div className="container px-6">
              <div className="text-center max-w-2xl mx-auto mb-14">
                {features.badge && (
                  <p className="text-sm uppercase tracking-wider text-primary font-medium mb-4">{features.badge}</p>
                )}
                <h2 className="text-3xl sm:text-4xl font-display font-bold">
                  {features.title}{" "}
                  {features.highlight && <span className="text-gradient">{features.highlight}</span>}
                </h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {features.items.map((f: any, i: number) => {
                  const Icon = ICONS[f.icon] || Sparkles;
                  const cardContent = (
                    <>
                      <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="text-lg font-sans font-medium mb-2 group-hover:text-primary transition-colors">{f.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                      {f.link && (
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                          {isAr ? "اعرف المزيد" : "Learn more"} <ArrowRight className="h-3 w-3" />
                        </span>
                      )}
                    </>
                  );

                  if (f.link) {
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.06 }}
                      >
                        <Link
                          to={localePath(f.link)}
                          className="group block p-6 rounded-2xl bg-card border border-border hover:border-primary/30 transition-all duration-300 shadow-card hover:shadow-lg h-full"
                        >
                          {cardContent}
                        </Link>
                      </motion.div>
                    );
                  }

                  return (
                    <motion.div
                      key={i}
                      className="group p-6 rounded-2xl bg-card border border-border hover:border-primary/30 transition-all duration-300 shadow-card hover:shadow-lg"
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.06 }}
                    >
                      {cardContent}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Pricing */}
        {pricing && (
          <section className="py-12 lg:py-16 bg-secondary/40">
            <div className="container px-6 max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">{pricing.title}</h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">{pricing.description}</p>
              {pricing.cta_text && (
                <Button variant="hero" size="lg" asChild>
                  <Link to={pricing.cta_link || "/contact"}>
                    {pricing.cta_text}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          </section>
        )}

        {/* Related Work / Portfolio */}
        {relatedWork.length > 0 && (
          <section className="py-12 lg:py-16 bg-secondary/40">
            <div className="container px-6">
              <div className="text-center max-w-2xl mx-auto mb-14">
                <p className="text-sm uppercase tracking-wider text-primary font-medium mb-4">
                  {t("Our Work", "أعمالنا")}
                </p>
                <h2 className="text-3xl sm:text-4xl font-display font-bold">
                  {t(`Projects Built with ${platformName}`, `مشاريع بنيت باستخدام ${platformName}`)}
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {relatedWork.map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                  >
                    <Link
                      to={localePath(`/work/${item.slug}`)}
                      className="group block rounded-2xl bg-card border border-border hover:border-primary/30 transition-all duration-300 shadow-card hover:shadow-lg overflow-hidden"
                    >
                      {item.featured_image && (
                        <div className="aspect-[16/10] overflow-hidden bg-secondary">
                          <img
                            src={item.featured_image}
                            alt={t(item.title, item.title_ar)}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="p-5">
                        <h3 className="font-sans font-medium group-hover:text-primary transition-colors mb-1">
                          {t(item.title, item.title_ar)}
                        </h3>
                        {(item.short_description || item.short_description_ar) && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {t(item.short_description || "", item.short_description_ar)}
                          </p>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQs */}
        {faqs && faqs.length > 0 && (
          <section className="py-12 lg:py-32">
            <div className="container px-6 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-display font-bold mb-10 text-center">
                {isAr ? "الأسئلة الشائعة" : "Frequently Asked Questions"}
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq: any, i: number) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-base">{faq.q || faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{faq.a || faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        <CTA />
      </main>
      <Footer />
    </div>
  );
}
